import type { TFunction } from 'i18next';
import type { ITaskLogEntry } from '../../interfaces/IScheduler.js';
import { STATUS_KEY } from './statusLabels.js';
import { LOCATION_STATUS_LABEL_KEY } from './locationStatusLabels.js';

// ---------------------------------------------------------------------------
// logDisplay — CLIENT-SIDE rendering of the structured, server-generated
// execution log (phase 2). The log is stored as `{ kind, data }`, so the web
// drawer's and the mobile sheet's timelines render each kind from `data` via
// i18n (`scheduler.panel.log.*`) and the log language follows the VIEWER, not
// the writer. Enum values (task / area status) resolve through their existing
// label keys, so the two never drift.
// ---------------------------------------------------------------------------

/** A localized, human-readable one-line description of a structured log entry. */
export function describeLogEntry(t: TFunction, entry: ITaskLogEntry): string {
  switch (entry.kind) {
    case 'status':
      return t('scheduler.panel.log.status', {
        from: t(`scheduler.status.${STATUS_KEY[entry.data.from]}`),
        to: t(`scheduler.status.${STATUS_KEY[entry.data.to]}`),
      });
    case 'progress':
      return t('scheduler.panel.log.progress', { from: entry.data.from, to: entry.data.to });
    case 'location': {
      const { from, to } = entry.data;
      if (to === undefined) return t('scheduler.panel.log.locationUnassigned');
      if (from !== undefined) {
        return t('scheduler.panel.log.locationChanged', {
          from: t(LOCATION_STATUS_LABEL_KEY[from]),
          to: t(LOCATION_STATUS_LABEL_KEY[to]),
        });
      }
      return t('scheduler.panel.log.locationAssigned', { status: t(LOCATION_STATUS_LABEL_KEY[to]) });
    }
    case 'check':
      return entry.data.completed
        ? t('scheduler.panel.log.checkCompleted')
        : t('scheduler.panel.log.checkUncompleted');
    case 'checklist':
      return t('scheduler.panel.log.checklistAmended', { ...entry.data });
    case 'comment':
      return t('scheduler.panel.log.comment');
    case 'system':
      return entry.data.text;
    case 'stock':
      return entry.data.items
        .map((item) => describeStockItem(t, item, entry.data.actualDays))
        .join(' · ');
  }
}

type StockLogItem = Extract<ITaskLogEntry, { kind: 'stock' }>['data']['items'][number];

/**
 * One reconciled claim's line. The three outcomes are distinct facts, not
 * shades of the same one: the deduction landed, time had already covered the
 * claim (nothing to take), or the deduction was declined outright — the last
 * names its reason, because "not reconciled" without one reads as a bug.
 */
function describeStockItem(t: TFunction, item: StockLogItem, actualDays: number): string {
  if (item.skipped !== null) {
    return t('scheduler.panel.log.stockSkipped', {
      material: item.materialName,
      reason: t(`scheduler.panel.log.stockSkip.${item.skipped}`),
    });
  }
  if (item.deducted > 0) {
    return t('scheduler.panel.log.stockDeducted', {
      qty: item.deducted,
      unit: item.unit,
      material: item.materialName,
      claimed: item.claimed,
      byTime: item.consumedByTime,
      days: actualDays,
    });
  }
  return t('scheduler.panel.log.stockCovered', { material: item.materialName });
}
